import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { formatPrice, setCookie, showError } from '@/utils';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useMatch, useNavigate } from 'react-router-dom';
import routes from '@/routes';
import {
	usePaymentMethod,
	useShallowSelector,
	useSubscribed,
	useTrialDiscount
} from '@/hooks';
import { StampCheckmarkIcon } from '@/components/Icons/StampCheckmarkIcon';
import { useMedia } from '@/hooks/responsive';
import { CircleCheck } from '@/components/Icons';
import { fetchMe } from '@/store/auth/thunks';
import { TRIGGER_ACTIONS } from '@/store/editor';
import { PAYMENT_HANDLER, TRIAL_DAYS } from '@/config';
import { getPaymentModule } from '@/components/payment/getPaymentModule';
import { usePlans } from '@/hooks/price';
import ExtendedTermsDisclaimer from '@/components/ExtendedTermsDisclaimer';
import { fetchTransactions } from '@/store/editor/thunks';
import { Button } from '@/components/Button';

import { TrialPlan } from './TrialPlan';
import { usePlanDescriptions } from './hooks';
import { PAYMENT_BREAKDOWN_KEY } from '../PaymentBreakdownModal/constants';
import TrustPilotInfo from './TrustPilotInfo';
import EditedDocuments from './EditedDocuments';
import BannerBottom from './BannerBottom';
import { FastCheckoutImage } from './FastCheckoutImage';
import { TABS } from '../../my-account/constants';

const DISCOUNT_TIMER_MINUTES = 11;
const MINUTE_IN_MS = 60 * 1000;

const { PaymentElement } = getPaymentModule(PAYMENT_HANDLER);

export function FastCheckoutContent({
	bannerBottom = false,
	trustPilotData = false
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const fastEditor = !!useMatch(routes.fastCheckout);
	const fastDashboard = !!useMatch(routes.fastCheckoutDashboard);
	const mdDown = useMedia('mdDown');
	const dispatch = useDispatch();
	const plansData = usePlans();

	const bottomFormRef = useRef();

	const {
		trialPlan,
		loading: plansLoading,
		monthlyPlan,
		mainPlan
	} = plansData;

	const { currency, user, priceCurrency } = useShallowSelector(state => ({
		currency: state.auth.currency,
		priceCurrency: state.auth.priceCurrency,
		user: state.auth.user
	}));

	const isFastCheckoutRoute = fastEditor || fastDashboard;
	const subscribed = useSubscribed();
	const trialDiscount = useTrialDiscount();

	const [previewLoading, setPreviewLoading] = useState(false);

	const handlePaymentSuccess = () => {
		if (user?.hasGoogleAdId) {
			setCookie(PAYMENT_BREAKDOWN_KEY, true);
		}

		setCookie(TRIGGER_ACTIONS.RATE_US, true);
	};

	const { hasPaymentMethod } = usePaymentMethod();

	const [initialRenderTime] = useState(dayjs());
	const [paymentReady, setPaymentReady] = useState(false);
	const [showStickyButton, setShowStickyButton] = useState(false);

	const trialPlanPrice = formatPrice({
		amount: trialPlan?.baseAmount,
		currency: currency || trialPlan?.currency
	});

	const planDescriptions = usePlanDescriptions(monthlyPlan);

	const loading = plansLoading || !paymentReady;

	useEffect(() => {
		(async function updateTransactions() {
			try {
				setPreviewLoading(true);
				dispatch(fetchTransactions());
			} catch (error) {
				showError(error);
			} finally {
				setPreviewLoading(false);
			}
		})();
	}, []);

	useEffect(() => {
		if (!mdDown || loading) {
			setShowStickyButton(false);
			return;
		}

		const handleScroll = () => {
			const element = document.querySelector(
				'.adyen-checkout-pm-details-wrapper'
			);

			if (!element) return;

			const { top, bottom } = element.getBoundingClientRect();

			setShowStickyButton(top > window.innerHeight - 100 || bottom < 0);
		};

		handleScroll();

		document.addEventListener('scroll', handleScroll, true);
		window.addEventListener('resize', handleScroll);

		return () => {
			document.removeEventListener('scroll', handleScroll, true);
			window.removeEventListener('resize', handleScroll);
		};
	}, [mdDown, loading]);

	const loadingRender = (
		<Stack
			sx={{
				minHeight: 350,
				justifyContent: 'center',
				alignItems: 'center'
			}}
		>
			<CircularProgress />
		</Stack>
	);

	const previewRender = (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column'
			}}
		>
			<Box
				sx={{
					color: 'primary.main',
					mb: { xs: '12px', md: '24px' },
					alignItems: 'center',
					display: 'flex',
					gap: '8px'
				}}
			>
				<CircleCheck
					sx={{
						width: { xs: '24px', md: '28px' },
						height: { xs: '24px', md: '28px' }
					}}
				/>

				<Typography variant="body3" fontWeight={700}>
					{t('fastCheckout.header')}
				</Typography>
			</Box>
			<Box
				sx={{
					flexGrow: 1,
					pt: 0,
					paddingInlineEnd: { md: '20px' }
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: { xs: '100%', md: 482 },
						height: { xs: 316, md: 482 },
						p: '20px',
						borderRadius: '8px',
						bgcolor: 'neutral.150'
					}}
				>
					{previewLoading ? loadingRender : <FastCheckoutImage />}
				</Box>
			</Box>

			{!mdDown && trustPilotData && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
						width: '100%',
						py: '16px',
						px: '12px',
						pr: 0,
						bgcolor: 'common.white'
					}}
				>
					<TrustPilotInfo />
					<EditedDocuments />
				</Box>
			)}
		</Box>
	);

	useEffect(() => {
		const intervalId = setInterval(() => {
			const currentTime = dayjs();
			const startTime = dayjs(initialRenderTime);

			if (currentTime.diff(startTime, 'minutes') >= DISCOUNT_TIMER_MINUTES) {
				dispatch(fetchMe());
				clearInterval(intervalId);
			}
		}, MINUTE_IN_MS);

		return () => clearInterval(intervalId);
	}, [dispatch, initialRenderTime]);

	useEffect(() => {
		if (subscribed && isFastCheckoutRoute) {
			navigate(routes.myImages);
		}
	}, [subscribed, isFastCheckoutRoute]);

	function getButtonText() {
		if (trialDiscount) {
			return t('fastCheckout.startTrialDiscount');
		}

		return t(`fastCheckout.button`);
	}

	const buttonText = getButtonText();

	if (subscribed) {
		return null;
	}

	const content = (
		<>
			{loading && loadingRender}
			<Box
				sx={{
					'& form': { pr: 0 },
					mt: { xs: '32px', md: 0 },
					marginInlineStart: { md: '20px' },

					...(loading
						? {
								maxHeight: 0,
								overflow: 'hidden',
								opacity: 0
							}
						: {})
				}}
			>
				<Box mb="20px">
					<Stack
						sx={{
							pb: '20px',
							gap: { xs: '16px', md: '20px' }
						}}
					>
						<Typography
							variant="body3"
							sx={{
								fontWeight: { xs: 600, lg: 700 },
								maxWidth: '95%',
								display: 'inline-block'
							}}
						>
							{t('fastCheckout.title', { trialDays: TRIAL_DAYS })}
						</Typography>

						<Stack gap="12px">
							<Typography
								variant={mdDown ? 'body2' : 'body1'}
								fontWeight={700}
							>
								{t('fastCheckout.plans.descriptionsTitle', {
									trialDays: TRIAL_DAYS
								})}
							</Typography>

							<Stack gap={{ xs: '8px', md: '4px' }}>
								{planDescriptions.map(
									({ i18nKey, Icon, transValues }) => (
										<Box
											key={i18nKey}
											sx={{
												display: 'flex',
												alignItems: {
													xs: 'flex-start',
													md: 'center'
												},
												gap: '4px',
												color: 'text.secondary'
											}}
										>
											<Icon
												style={{
													width: '16px',
													height: '16px',
													flexShrink: 0
												}}
											/>

											<Typography
												variant={mdDown ? 'body0' : 'body1'}
												color="text.secondary"
												sx={{ whiteSpace: 'pre-line' }}
											>
												<Trans
													i18nKey={i18nKey}
													values={transValues}
													components={{ strong: <strong /> }}
												/>
											</Typography>
										</Box>
									)
								)}
							</Stack>
						</Stack>
					</Stack>

					{trialPlan && !trialDiscount && !plansLoading && (
						<TrialPlan
							title={t('fastCheckout.trialTitle')}
							trialPlan={trialPlan}
							trialPlanPrice={trialPlanPrice}
							currency={currency}
							mainPlan={mainPlan}
						/>
					)}

					{trialDiscount && trialPlan && (
						<Stack
							alignItems="center"
							direction="row"
							justifyContent="space-between"
							pt={{ xs: '12px', md: '16px' }}
						>
							<Box alignItems="center" display="flex" gap="8px">
								<StampCheckmarkIcon
									sx={{
										backgroundColor: 'lime',
										borderRadius: '50%',
										fontSize: '32px',
										padding: '4px'
									}}
								/>

								<Typography
									variant={mdDown ? 'body2' : 'body3'}
									fontWeight={mdDown ? 500 : 400}
								>
									{t('fastCheckout.trialTitle')}
								</Typography>
							</Box>
							<Typography
								fontWeight={700}
								sx={{
									backgroundColor: 'lime',
									borderRadius: '4px',
									padding: '4px 8px',
									lineHeight: '28px',
									fontSize: { md: '24px' }
								}}
								variant="body3"
							>
								{formatPrice({
									amount: 0,
									currency: currency || trialPlan?.currency
								})}
							</Typography>
						</Stack>
					)}
				</Box>

				{!plansLoading && trialPlan && (
					<Box>
						<PaymentElement
							isFastCheckout
							mode="checkout"
							hasPaymentMethod={hasPaymentMethod}
							priceId={trialPlan?.handlerId}
							mainPriceId={mainPlan?.handlerId}
							currency={currency || priceCurrency}
							onSuccess={() => {
								handlePaymentSuccess();

								const successRedirect = `${routes.myImages}?success=true`;

								navigate(successRedirect);
							}}
							onPaymentFormReady={payload => setPaymentReady(payload)}
							onError={error => {
								/* eslint-disable-next-line no-console */
								console.error(error);
								navigate(routes.myAccount, {
									state: { tab: TABS.BILLING }
								});
							}}
							checkoutMessage={buttonText}
							termsNode={
								<>
									<Box ref={bottomFormRef} height={1} />
									<ExtendedTermsDisclaimer
										monthlyPlan={monthlyPlan}
										trialPlan={trialPlan}
										isTrialPlan
										buttonLabel={buttonText}
									/>
								</>
							}
							preload
						/>
					</Box>
				)}

				{mdDown && showStickyButton && (
					<Box
						sx={{
							position: 'fixed',
							borderTop: '1px solid #E8E8E8',
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 50,
							background: 'white',
							p: '16px',
							pb: '24px'
						}}
					>
						<Button
							sx={{ width: '100%' }}
							variant="contained"
							onClick={() => {
								bottomFormRef.current?.scrollIntoView({
									behavior: 'smooth',
									block: 'end'
								});
							}}
						>
							{t('common.downloadNow')}
						</Button>
					</Box>
				)}
			</Box>
		</>
	);

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: {
						xs: 'column',
						md: 'row'
					},
					width: '100%'
				}}
			>
				{!mdDown && previewRender}

				<Box
					sx={{
						pb: '20px'
					}}
				>
					{mdDown && previewRender}

					{content}
				</Box>
			</Box>

			{bannerBottom && <BannerBottom />}
		</>
	);
}
